/**
 * Nominal brands for the two shapes `CALENDAR_DATE_ANCHORING` distinguishes —
 * a calendar date and an instant — so the TypeScript compiler can reject the
 * bug that rule catalogs four separate times (issues #2520, #2545, `3146a37cb`):
 * a bare `YYYY-MM-DD` written into a `timestamptz` column, landing at UTC
 * midnight and reading back one Central calendar day early.
 *
 * The generated `database.types.ts` types a `date` column and a `timestamptz`
 * column identically (`string | null`) — see that file's own header — so
 * today the compiler cannot see this distinction at all; every assignment
 * type-checks regardless of which shape the value actually is. These brands
 * exist to give the two shapes distinct TYPES so a mismatch is a `tsc` error
 * instead of a runtime PostgREST/pg_graphql failure three time zones later.
 *
 * - {@link CalendarDate} — a wall-clock day, `YYYY-MM-DD`, no time, no offset.
 *   What a user picks in a date field.
 * - {@link Instant} — a point in time: a full RFC 3339 timestamp with a time
 *   and an offset (or `Z`). What a `timestamptz` column actually stores.
 *
 * Minting follows the SAME convention as the CRM id brands
 * (`lib/core/chat/tools/id-resolution/branded-ids.ts`): a Zod branded schema
 * attaches the brand internally via `.parse()`, so there is no `as` cast
 * anywhere in this module or its callers (per `main-03`). The ONLY sanctioned
 * way to mint one of these from a plain `string` is {@link asCalendarDate} /
 * {@link asInstant} (both throw `ZodError` on a malformed value) — or the
 * existing anchor primitives in `calendar-date-anchor.ts` /
 * `due-in-days.ts`, which mint an {@link Instant} as part of doing the actual
 * anchoring. Never hand-cast a `string` to either brand.
 */

import { z } from "zod";

const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True iff `y-m-d` (already regex-shaped) is a real calendar date — rejects `2026-02-30`, `2026-13-01`, etc. */
function isRealCalendarDate(value: string): boolean {
  const [year, month, day] = value.split("-").map(Number);
  const roundTrip = new Date(Date.UTC(year, month - 1, day));
  return (
    roundTrip.getUTCFullYear() === year &&
    roundTrip.getUTCMonth() === month - 1 &&
    roundTrip.getUTCDate() === day
  );
}

/**
 * A wall-clock calendar day — `YYYY-MM-DD`, no time, no offset. Nominal — not
 * interchangeable with {@link Instant}: a `CalendarDate` is not itself a valid
 * value for a `timestamptz` column, it must go through
 * `anchorBareCalendarDate` (or `anchorDueInDaysCentral`) first.
 */
export const CalendarDateSchema = z
  .string()
  .regex(BARE_DATE_RE, "must be a bare YYYY-MM-DD calendar date with no time or offset")
  .refine(isRealCalendarDate, "not a real calendar date")
  .brand<"CalendarDate">();
export type CalendarDate = z.infer<typeof CalendarDateSchema>;

/**
 * Mint a {@link CalendarDate} from a runtime string — the boundary
 * constructor for user-picked-date input (a form field, a Slack modal value,
 * a tool param) before it flows toward an anchor primitive. Throws `ZodError`
 * on anything that is not a real bare `YYYY-MM-DD` date (wrong shape, or a
 * shape-matching but impossible date like `2026-02-30`).
 */
export function asCalendarDate(value: string): CalendarDate {
  return CalendarDateSchema.parse(value);
}

/**
 * A point in time — full RFC 3339, time and offset (or `Z`) required. What a
 * `timestamptz` column actually stores. Nominal — not interchangeable with
 * {@link CalendarDate}.
 */
export const InstantSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/,
    "must be a full RFC 3339 instant (date, time, and an offset or Z)"
  )
  .refine((value) => !Number.isNaN(new Date(value).getTime()), "does not parse as a valid instant")
  .brand<"Instant">();
export type Instant = z.infer<typeof InstantSchema>;

/**
 * Mint an {@link Instant} from a runtime string that is ALREADY a full
 * timestamp — e.g. `new Date().toISOString()` at a server-stamped write site
 * (a mark-won/mark-lost close, a status-transition timestamp). Throws
 * `ZodError` on a bare calendar date or anything else that is not a complete
 * RFC 3339 instant; a bare date belongs in {@link asCalendarDate} and then
 * `anchorBareCalendarDate`, not here.
 */
export function asInstant(value: string): Instant {
  return InstantSchema.parse(value);
}
